import type {
    ConnectorDatasetId,
    ConnectorPatientId,
    ConnectorVersionToken,
} from './PersistenceRef';

/**
 * The ingest endpoint's request cap — DicomController.MaxUploadRequestBytes
 * on the connector, mirrored by IMPORT_LIMITS.maxBytes in the import
 * wizard. A series over this limit cannot be uploaded in one atomic
 * request and is refused at save: chunking would make a half-uploaded
 * series indistinguishable from a complete one on the server.
 */
export const MAX_UPLOAD_REQUEST_BYTES = 512 * 1024 * 1024;

/** An image-series row as the connector reports it in the patient detail. */
export interface PersistedImageSeries {
    readonly datasetId: ConnectorDatasetId;
    readonly seriesInstanceUid: string;
    readonly version: ConnectorVersionToken;
    /**
     * The row's stored display name. The saver compares it against the local
     * one so a rename made between a failed adoption and its retry is still
     * sent — the re-upload is a server-side no-op and would otherwise drop it.
     */
    readonly seriesDescription: string | null;
}

/**
 * An image-series row as the upload response reports it: the patient-detail
 * identity plus whether THIS request created the row. `wasNew` is what lets
 * a caller holding no ref tell its own creation (whose version token it may
 * write with) from an adoption of a row that predates the request (whose
 * token was never acknowledged in this session).
 */
export interface UploadedImageSeries extends PersistedImageSeries {
    readonly wasNew: boolean;
}

/** What one upload reported. */
export interface UploadedImageBatch {
    readonly rows: readonly UploadedImageSeries[];
    /**
     * True when the upload needed more than one attempt. An earlier attempt
     * may have stored the series before failing, so a row reported with
     * `wasNew` false may be this save's own creation rather than another
     * session's row.
     */
    readonly afterInterruptedAttempt: boolean;
}

/**
 * Port to the connector-next image persistence endpoints. Failures are
 * the typed errors in PersistenceErrors.ts.
 *
 * Uploading is the standard DICOM ingest: one multipart request is
 * atomic on the server and idempotent on retry (already-present SOP
 * Instance UIDs are skipped), and the server routes purely by the DICOM
 * PatientID inside the files — the CALLER must verify the files belong
 * to the loaded patient before uploading.
 */
export interface ImagePersistenceGateway {
    /**
     * POST /api/dicom/instances — one atomic, retry-idempotent batch.
     * Returns the image-series rows the batch touched (new AND
     * already-present, so a retried upload whose first response was lost
     * still receives the row) — the identity the caller adopts, with no
     * follow-up query and no window for one to fail in — and whether the
     * upload needed more than one attempt. A connector that predates
     * per-dataset reporting returns no rows; the caller falls back to
     * listPatientImageSeries.
     */
    uploadInstances(files: readonly Blob[]): Promise<UploadedImageBatch>;

    /** GET /api/patients/{id} — the patient's image series rows. */
    listPatientImageSeries(
        patientId: ConnectorPatientId
    ): Promise<readonly PersistedImageSeries[]>;

    /**
     * PATCH /api/images/{id} — renames the series (row display metadata
     * only; stored files untouched). Version-guarded.
     */
    updateLabel(
        id: ConnectorDatasetId,
        ifMatch: ConnectorVersionToken,
        seriesDescription: string
    ): Promise<{ readonly version: ConnectorVersionToken }>;
}
